import {
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import {NgClass, NgForOf, NgIf, DatePipe} from '@angular/common';
import {MatDialog} from '@angular/material/dialog';
import {TranslateModule, TranslateService} from '@ngx-translate/core';

import {ExternalHeaderComponent} from '../../../shared/external-header/external-header.component';
import {FooterComponent} from '../../../shared/footer/footer.component';
import {LoadMaskComponent} from '../../../shared/load-mask/load-mask.component';
import {ButtonComponent} from '../../../shared/button/button.component';

import {BIPPERZ_BREAKPOINTS, BreakpointsService} from '../../../shared/services/breakpoint.service';
import {ImageService} from '../../../shared/services/image.service';
import {VideoService} from '../../../shared/services/video.service';
import {ShowPopupService} from '../../../shared/services/show-popup.service';
import {AgencyDashboardPageService} from './agency-dashboard-page.service';
import {RegistrationPageService} from '../../registration-pages/registration-page/registration-page.service';

import {AddNewCandidateModalComponent} from '../../../modals/add-new-candidate-modal/add-new-candidate-modal.component';
import {CvResultModalComponent} from '../../../modals/cv-result-modal/cv-result-modal.component';
import languagesData from '../../../../assets/language.json';
import countries from '../../../../assets/country.json';

import {
  ButtonSize,
  ButtonState,
  ButtonType, CandidateDocsItem, DocRow,
  IMaterial,
  IUser, MaterialType,
} from '../../../../assets/types/dtoTypes';
import {FormsModule} from "@angular/forms";
import {AzureService} from "../../../shared/services/azure.service";
import {
  TranslateDocumentModalComponent
} from "../../../modals/translate-document-modal/translate-document-modal.component";
import {
  CertificateUploadDialogComponent
} from "../../registration-pages/registration-page/forms/resume-form/certificate-upload-dialog/certificate-upload-dialog.component";
import {buildCountryMap, buildLanguageMap, getFlagEmoji, getFlagSrc} from 'src/app/shared/utils/flag.util';
import {extractFilename} from "../../../shared/utils/url-sas.util";
import {finalize, forkJoin, of, switchMap} from "rxjs";
import {ActivatedRoute, Router} from "@angular/router";
import {AuthenticationService} from "../../../shared/services/authentication.service";
import {mapDocRows} from "../../../shared/utils/docs-row.mapper";
import {CandidatePrepService} from "../../../shared/services/candidate-prep.service";
import {downloadUrl} from "../../../shared/utils/download.util";
import {ProfilePageService} from "../user/profile-page/profile-page.service";

@Component({
  selector: 'app-agency-dashboard-page',
  standalone: true,
  templateUrl: './agency-dashboard-page.component.html',
  styleUrl: './agency-dashboard-page.component.scss',
  imports: [
    ExternalHeaderComponent,
    FooterComponent,
    LoadMaskComponent,
    NgClass,
    NgIf,
    NgForOf,
    ButtonComponent,
    TranslateModule,
    DatePipe,
    FormsModule,
  ],
})
export class AgencyDashboardPageComponent implements OnInit {

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly profilePageService = inject(ProfilePageService);
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly imageService = inject(ImageService);
  private readonly videoService = inject(VideoService);
  private readonly showPopupService = inject(ShowPopupService);
  private readonly registrationPageService = inject(RegistrationPageService);
  private readonly agencyDashboardPageService = inject(AgencyDashboardPageService);
  private readonly breakpointsService = inject(BreakpointsService);
  private readonly azureService = inject(AzureService);
  private readonly candidatePrep = inject(CandidatePrepService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authenticationService = inject(AuthenticationService);

  searchTerm = '';
  isLoading = false;
  isLoadingDocs = false;
  isMobile = false;

  candidates: IUser[] = [];
  filteredCandidates: IUser[] = [];
  selectedCandidate: IUser | null = null;

  docsByMaterialId: Record<string, CandidateDocsItem> = {};
  rowsByMaterialId: Record<string, DocRow[]> = {};

  private readonly languageCountryMap = buildLanguageMap(languagesData.languages);
  private readonly countryCodeMap = buildCountryMap(countries);

  selectedTab: 'profile' | 'documents' = 'profile';

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonState = ButtonState;
  protected readonly ButtonType = ButtonType;

  constructor() {
    this.observeBreakpoints();
  }

  ngOnInit(): void {
    const token = this.route.snapshot.queryParamMap.get('token');
    if (token) {
      this.authenticationService.setAccessToken(token);
      this.router.navigate([], { queryParams: {}, replaceUrl: true });
    }
    this.loadCandidates();
  }

  private observeBreakpoints(): void {
    this.breakpointsService
      .observe([BIPPERZ_BREAKPOINTS.s, BIPPERZ_BREAKPOINTS.m_down])
      .subscribe(result => {
        this.isMobile = !!(result.s || result.m_down);
      });
  }

  private async loadCandidates(): Promise<void> {
    this.isLoading = true;

    this.agencyDashboardPageService
      .getUsers()
      .pipe(
        switchMap(users => {
          const prepared$ = users.map(u => this.candidatePrep.prepare(u));
          return prepared$.length ? forkJoin(prepared$) : of([]);
        }),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: prepared => {
          this.candidates = prepared;
          this.filteredCandidates = [...prepared];
          this.selectedCandidate = this.filteredCandidates[0] ?? null;
          if (this.selectedCandidate?.id) this.loadCandidateDocuments(this.selectedCandidate.id);
          this.cdr.detectChanges();
        },
        error: err => console.error('Failed to load candidates:', err),
      });
  }

  selectCandidate(candidate: IUser): void {
    this.selectedCandidate = candidate;
    if (candidate?.id) this.loadCandidateDocuments(candidate.id);
  }

  onFileSelected(event: Event, id: string): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;
    this.setImageFile(file, id);
  }

  private setImageFile(file: File, id: string): void {
    this.isLoading = true;

    this.azureService
      .updateProfileImage(id, file)
      .pipe(
        switchMap(updatedUser => {
          if (!this.selectedCandidate) return of(updatedUser);
          return this.imageService.appendSasTokenToProfileImage(this.selectedCandidate);
        }),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: updated => {
          if (updated?.profileImageUrl && this.selectedCandidate) {
            this.selectedCandidate.profileImageUrl = updated.profileImageUrl;
          }
          this.cdr.detectChanges();
        },
        error: err => console.error('Failed to update profile image:', err),
      });
  }

  addNewCandidate(): void {
    const dialogRef = this.dialog.open(AddNewCandidateModalComponent, {
      width: 'auto',
      height: 'auto',
      disableClose: false,
    });

    dialogRef
      .afterClosed()
      .subscribe(async (created: IUser | null | undefined) => {
        if (!created?.id) return;

        try {
          const prepared = await this.candidatePrep.prepare(created);
          this.candidates = [prepared, ...this.candidates];
          this.onSearchChange();
          this.selectedCandidate = prepared;
          this.cdr.detectChanges();
        } catch (err) {
          console.error('Failed to add candidate:', err);
        }
      });
  }

  editCandidate(candidate: IUser): void {
    const dialogRef = this.dialog.open(CvResultModalComponent, {
      disableClose: true,
      panelClass: 'cv-modal-panel',
      data: {
        source: 'edit-candidate',
        firstname: candidate.firstname,
        lastname: candidate.lastname,
        location: candidate.location,
        profileImage: candidate.profileImageUrl,
        userId: candidate.id,
        bio: candidate.motivation,
        workExperience: candidate.workExperience,
        education: candidate.education,
        languages: candidate.languages,
      },
    });

    dialogRef
      .afterClosed()
      .subscribe((parsedCV: Partial<IUser>) => {
        if (!parsedCV || !this.selectedCandidate?.id) return;

        const candidateData = {
          motivation: parsedCV.motivation,
          education: parsedCV.education,
          workExperience: parsedCV.workExperience,
          languages: parsedCV.languages,
          country: parsedCV.location,
          profileImageUrl: parsedCV.profileImageUrl,
        };

        this.registrationPageService
          .updateUser(this.selectedCandidate.id, candidateData)
          .subscribe({
            next: () => {
              const updated: IUser = {
                ...this.selectedCandidate!,
                firstname: parsedCV.firstname ?? this.selectedCandidate!.firstname,
                lastname: parsedCV.lastname ?? this.selectedCandidate!.lastname,
                location: parsedCV.location ?? this.selectedCandidate!.location,
                motivation: parsedCV.motivation ?? this.selectedCandidate!.motivation,
                education: parsedCV.education ?? this.selectedCandidate!.education,
                workExperience: parsedCV.workExperience ?? this.selectedCandidate!.workExperience,
                languages: parsedCV.languages ?? this.selectedCandidate!.languages,
                profileImageUrl: parsedCV.profileImageUrl ?? this.selectedCandidate!.profileImageUrl,
              };

              this.selectedCandidate = { ...updated };
              this.candidates = this.candidates.map(c => (c.id === updated.id ? { ...updated } : c));
              this.filteredCandidates = this.filteredCandidates.map(c => (c.id === updated.id ? { ...updated } : c));

              this.onSearchChange();

              this.showPopupService.showSuccessPopup({
                title: this.translate.instant('candidates.candidateUpdateSuccess'),
              });

              this.cdr.detectChanges();
            },
            error: err => console.error(err),
          });
      });
  }

  deleteCandidate(candidateId: string): void {
    if (!candidateId) return;

    this.showPopupService
      .showConfirmationPopup({ status: 'warning', translationKey: 'deleteCandidate' })
      .pipe(
        switchMap(confirmed => (confirmed ? this.agencyDashboardPageService.deleteUser(candidateId) : of(null))),
      )
      .subscribe(res => {
        if (res === null) return;
        this.candidates = this.candidates.filter(c => c.id !== candidateId);
        this.selectedCandidate = this.candidates[0] || null;

        this.showPopupService.showSuccessPopup({
          title: this.translate.instant('common.deleted'),
          message: this.translate.instant('deleteCandidate.success'),
        });
      });
  }

  deleteMaterial(material: IMaterial): void {
    if (!material?.id || !this.selectedCandidate) return;

    this.showPopupService
      .showConfirmationPopup({ status: 'warning', translationKey: 'deleteMaterial' })
      .pipe(
        switchMap(confirmed => {
          if (!confirmed) return of(null);
          this.selectedCandidate!.materials = (this.selectedCandidate!.materials || []).filter(m => m.id !== material.id);
          return this.agencyDashboardPageService.deleteMaterial(material.id);
        }),
      )
      .subscribe({
        next: res => {
          if (res === null) return;
          this.showPopupService.showSuccessPopup({
            title: this.translate.instant('common.deleted'),
            message: this.translate.instant('deleteMaterial.success'),
          });
        },
        error: err => console.error('Failed to delete material:', err),
      });
  }

  async downloadMaterial(material: IMaterial): Promise<void> {
    if (!this.selectedCandidate) return;
    window.open(material.url, '_blank');
  }

  async viewMaterial(material: IMaterial): Promise<void> {
    if (!this.selectedCandidate) return;
    window.open(material.url, '_blank');
  }

  private loadCandidateDocuments(candidateId: string): void {
    this.isLoadingDocs = true;

    this.agencyDashboardPageService
      .getCandidateDocuments(candidateId)
      .pipe(
        finalize(() => (this.isLoadingDocs = false)),
      )
      .subscribe({
        next: docs => {
          this.docsByMaterialId = {};
          this.rowsByMaterialId = {};

          for (const d of docs || []) {
            this.docsByMaterialId[d.materialId] = d;
            this.rowsByMaterialId[d.materialId] = mapDocRows(d);
          }

          this.cdr.detectChanges();
        },
        error: e => {
          console.error('getCandidateDocuments failed:', e);
          this.docsByMaterialId = {};
          this.rowsByMaterialId = {};
          this.cdr.detectChanges();
        },
      });
  }

  getSignedRows(materialId: string): DocRow[] {
    return (this.rowsByMaterialId[materialId] || []).filter(r => r.kind === 'SIGNED');
  }

  openSignedUploadDialog(material: IMaterial): void {
    if (!this.selectedCandidate?.id) return;

    const dialogRef = this.dialog.open(CertificateUploadDialogComponent, {
      width: '600px',
      data: { userId: this.selectedCandidate.id },
    });

    dialogRef
      .afterClosed()
      .pipe(
        switchMap(result => {
          if (!result?.url?.trim()) return of(null);
          return this.videoService.appendSasTokenToVideos(this.selectedCandidate!.id, result.url).pipe(
            switchMap(fileUrl => this.agencyDashboardPageService.uploadSigned(material.id, fileUrl)),
          );
        }),
      )
      .subscribe({
        next: res => {
          if (res === null) return;
          this.loadCandidateDocuments(this.selectedCandidate!.id);
        },
        error: e => console.error('uploadSigned failed:', e),
      });
  }

  hasTranslation(materialId: string): boolean {
    const rows = this.rowsByMaterialId[materialId] || [];
    return rows.some(r => r.kind === 'TRANSLATED');
  }

  fileIcon(extOrContentType: string): 'pdf' | 'doc' {
    const s = (extOrContentType || '').toLowerCase();
    return s.includes('pdf') || s.endsWith('.pdf') ? 'pdf' : 'doc';
  }


  openCertificateListDialog(): void {
    const dialogRef = this.dialog.open(CertificateUploadDialogComponent, {
      width: '600px',
      data: { userId: this.selectedCandidate!.id },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!(result?.title && result.url?.trim())) return;

      this.videoService.appendSasTokenToVideos(this.selectedCandidate!.id, result.url).subscribe(fileUrl => {
        const newMaterial: Partial<IMaterial> = {
          title: result.title,
          userId: this.selectedCandidate!.id,
          url: fileUrl,
          description: result.description || 'n/a',
          type: MaterialType.OTHER,
        };

        this.profilePageService.createNewMaterial(newMaterial).subscribe(created => {
          if (!created?.id) return;

          this.selectedCandidate!.materials ??= [];
          this.selectedCandidate!.materials.unshift(created);

          this.upsertOriginalRowForMaterial(created);

          this.selectedTab = 'documents';

          this.loadCandidateDocuments(this.selectedCandidate!.id);

          this.cdr.detectChanges();
        });
      });
    });
  }

  private upsertOriginalRowForMaterial(material: IMaterial) {
    const rows = this.rowsByMaterialId[material.id] || [];
    const filename = material.title?.trim() || extractFilename(material.url);
    const contentType =
      filename.toLowerCase().endsWith('.pdf')
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

    const originalRow = {
      kind: 'ORIGINAL',
      name: filename,
      url: material.url,
      contentType,
      when: new Date().toISOString(),
      label: 'Uploaded',
    } as DocRow;

    const idx = rows.findIndex(r => r.kind === 'ORIGINAL');
    if (idx >= 0) rows[idx] = originalRow; else rows.unshift(originalRow);
    this.rowsByMaterialId[material.id] = rows;
  }



  async downloadUrl(url: string, filename?: string, _contentType?: string): Promise<void> {
    return downloadUrl(url, filename, _contentType);
  }

  getOriginalRow(materialId: string): DocRow | null {
    return (this.rowsByMaterialId[materialId] || []).find(r => r.kind === 'ORIGINAL') ?? null;
  }

  getTranslationRows(materialId: string): DocRow[] {
    return (this.rowsByMaterialId[materialId] || []).filter(r => r.kind === 'TRANSLATED');
  }

  openTranslateModule(material: IMaterial): void {
    if (!material?.id) return;

    const originalRow = this.getOriginalRow(material.id);
    const fileName = originalRow?.name ?? material.title?.trim() ?? extractFilename(material.url);
    const uploadDate = originalRow?.when ?? null;

    const ref = this.dialog.open(TranslateDocumentModalComponent, {
      data: {
        materialId: material.id,
        candidateId: this.selectedCandidate?.id,
        fileName,
        uploadDate,
      },
      panelClass: 'no-padding-dialog',
      disableClose: true,
    });

    ref
      .afterClosed()
      .subscribe(result => {
        if (result?.refresh && this.selectedCandidate?.id) {
          this.loadCandidateDocuments(this.selectedCandidate.id);
        }
      });
  }

  onSearchChange(): void {
    const term = this.searchTerm.toLowerCase().trim();

    this.filteredCandidates = this.candidates.filter(c => {
      const fullName = `${c.firstname} ${c.lastname}`.toLowerCase();
      return fullName.includes(term);
    });

    if (!this.filteredCandidates.some(c => c.id === this.selectedCandidate?.id)) {
      this.selectedCandidate = this.filteredCandidates[0] || null;
    }
  }

  getFlagSrc(lang: string): string {
    return getFlagSrc(lang, this.languageCountryMap);
  }
  getFlagEmoji(loc?: string | null): string {
    return getFlagEmoji(loc ?? null, this.countryCodeMap);
  }

}
